// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { METERS_PER_DEGREE_LAT } from '@/services/geo';
import mapColors from '@/theme/map';

import {
  distanceToPolylineMeters,
  distanceToRingMeters,
  distanceToSegmentMeters,
  type LatLon,
} from './tileMath';

/**
 * The mapped boundaries a hole can be drawn from, and the rules for picking the ones that
 * belong to one hole's picture.
 *
 * Pure geometry and palettes, no React: the hole view, the admin analysis map and the
 * course-guide drawing all colour and order the same shapes, and the only way three surfaces
 * agree on what a bunker looks like is to read it from one place.
 */

/** A stored boundary: a bunker, a pond, a green surface, a fairway, a belt of trees. */
export type HoleShape = {
  id: string;
  kind: string;
  /** The outer ring, in order. */
  ring: LatLon[];
};

/**
 * The trouble a player aims around, which is what the in-round overlay draws.
 *
 * The green is in here because it is an object with an edge: knowing where the surface
 * starts and stops is the difference between a number to the middle and a club selection.
 */
export const HAZARD_KINDS = ['bunker', 'water', 'green'];

/**
 * Ground cover. Cut from the whole course at once rather than per hole, so one fairway
 * component routinely spans two holes and a tree belt runs the length of a stretch.
 */
export const COVER_KINDS = ['fairway', 'trees'];

/**
 * Roads and cart paths: OSM highway lines buffered by their width into thin rings (and, later,
 * the imagery analyser's own detections, stored the same way). Selected like cover — a road
 * runs past several holes just as a tree belt does — but kept apart from it, because a hole's
 * own fairway rows decide its fairway and say nothing about which roads cross it.
 *
 * Drawn for orientation only: under everything, never a distance chip, never a framing point.
 */
export const PATH_KINDS = ['path'];

/**
 * Kinds the hole view draws. A stored boundary of any other kind is data for the admin map,
 * not a shape the player sees.
 */
export const HOLE_VIEW_KINDS = [...HAZARD_KINDS, ...COVER_KINDS, ...PATH_KINDS];

/** Draw order. Paths under everything, then cover, then hazards, then the green on top. */
export const SHAPE_DEPTH: Record<string, number> = {
  path: -1,
  trees: 0,
  fairway: 1,
  water: 2,
  bunker: 3,
  green: 4,
};

/**
 * Boundary colours drawn over satellite imagery: a stroke with the same colour behind it at
 * low opacity, bright enough to read against grass, water and sand alike. The values live in
 * theme/map.ts with the rest of the map palette.
 */
export const OVERLAY_COLORS: Record<string, string> = mapColors.overlay;

/** Flat cartographic fills, in the spirit of a paper yardage book rather than the imagery. */
export const DRAWING_FILLS: Record<string, { fill: string; stroke: string }> = mapColors.drawing;

/** What a drawing shows where nothing else is mapped: everything is rough until proven otherwise. */
export const ROUGH_FILL = mapColors.rough;

/**
 * How far outside the tees and the green a boundary may sit and still belong to the hole.
 *
 * Wide enough for a greenside bunker, the pond short of the green and the trees down either
 * side; narrow enough that the next hole's fairway does not paint itself over this one.
 */
export const HOLE_FRAME_MARGIN_METERS = 80;

/**
 * Fairway and green: the mown ground a hazard is placed to guard.
 *
 * A tee is deliberately not in here. Tees are stored as bare centre points far more often than
 * as boundaries, so a tee would contribute a play area on some courses and nothing on others —
 * and the bunkers beside a tee are the next hole's anyway.
 */
export const PLAY_AREA_KINDS = ['fairway', 'green'];

/**
 * How far off this hole's own fairway or green a hazard may sit and still be drawn on it.
 *
 * Measured against Holms Golfklubb's 18 holes and 80 OSM bunkers, scored against the hole each
 * one is filed under: at 30 m every bunker lands on its own hole and none lands on a
 * neighbour's. The band is wide — nothing foreign appears anywhere from 5 m up to 30 m — which
 * is the point of measuring from the mown ground rather than from a line. A fairway polygon
 * already carries the hole's width and its dogleg; a corridor has to guess both, and at the
 * 80 m guess {@link HOLE_FRAME_MARGIN_METERS} makes, 26 of the 106 bunkers drawn across the
 * course belonged to another hole.
 *
 * Tighter is not safer. `segmentation.py`'s BUNKER_PLAY_MARGIN_M tried 20 m against Chalmers
 * and found it demoted real bunkers, because plenty sit behind a strip of rough rather than on
 * the fairway's own edge; the same sweep here misses two of eighty at 20 m and none at 30 m.
 */
export const PLAY_AREA_MARGIN_METERS = 30;

/**
 * The fallback margin, for a hole whose fairway and green are not mapped as boundaries.
 *
 * Only ever applied together with the nearest-hole test below, which is what makes a number
 * this generous safe: 50 m of corridor on its own drew three foreign bunkers in the same
 * measurement, and 50 m of corridor that also has to beat every other hole's centreline drew
 * none.
 */
export const CENTRELINE_MARGIN_METERS = 50;

/** A hole's playing line as OSM drew it (or as the import derived it), tee first. */
export type HoleCentreline = {
  holeNumber: number;
  path: LatLon[];
  /**
   * Which producer drew it — `derived` for DerivedCentrelineWriter's, anything else for a line
   * a person or OSM drew. Optional because a caller that holds only one line per hole has
   * nothing to choose between; see {@link playingLineFor}.
   */
  source?: string;
};

/**
 * A stored line that is not the playing line: where a hole stops being in play, along the
 * stakes an admin recorded. Drawn as a stroke, never filled — it has no inside.
 */
export type HoleLine = {
  id: string;
  kind: string;
  holeNumber: number;
  /** In order along the stakes. */
  path: LatLon[];
  source?: string;
};

/** The one line kind drawn today; `centreline` is handled as the playing line instead. */
export const OUT_OF_BOUNDS_KIND = 'out-of-bounds';

/** DerivedCentrelineWriter's own source tag, as the features endpoint reports it. */
const DERIVED_SOURCE = 'derived';

/**
 * The line a hole is played along, or an empty path when none is stored.
 *
 * A hole can hold more than one: DerivedCentrelineWriter skips a hole that already has a drawn
 * line, but a course imported from OSM *after* being analysed keeps both. A drawn line wins,
 * which is the same order FlyoverHoleResolver picks in — so what the map shows is the line the
 * flyover will actually fly, not a second opinion about it.
 */
export const playingLineFor = (
  centrelines: HoleCentreline[],
  holeNumber: number
): LatLon[] =>
  centrelines
    .filter((line) => line.holeNumber === holeNumber && line.path.length > 0)
    .sort((a, b) => Number(a.source === DERIVED_SOURCE) - Number(b.source === DERIVED_SOURCE))[0]
    ?.path ?? [];

/**
 * What {@link shapesNearHole} knows about the hole beyond its tees and green.
 *
 * Every field is optional and each one independently upgrades the answer, because how well a
 * course is mapped varies hole by hole: nationally about half of the courses that have bunkers
 * have their fairways drawn, three quarters have their greens. A hole that has neither still
 * gets the tee-to-green rule it has always had.
 */
export type HoleSelectionContext = {
  /**
   * This hole's own cover rows — the fairway and trees filed under this hole number.
   *
   * Supplied, these *are* the cover this hole draws, and the geometric fallback below is not
   * consulted. See {@link shapesNearHole}.
   */
  coverAreas?: HoleShape[];
  /**
   * This hole's own road and path rows. Supplied, these are the paths this hole draws; empty,
   * any path whose bounding box reaches the hole's frame is drawn instead — the same rule as
   * cover, kept separate so owning a fairway does not decide which roads a hole shows.
   */
  pathAreas?: HoleShape[];
  /** This hole's own fairway and green boundaries — the rows filed under this hole number. */
  playAreas?: HoleShape[];
  /** Every other hole's, so a hazard between two of them can be given to the nearer. */
  otherPlayAreas?: HoleShape[];
  /** This hole's centreline. */
  centreline?: LatLon[] | null;
  /** Every other hole's centreline, so a hazard can be given to whichever line is nearest. */
  otherCentrelines?: LatLon[][];
  /** Overrides {@link HOLE_FRAME_MARGIN_METERS} for cover and for the tee-to-green fallback. */
  marginMeters?: number;
};

const isFinitePoint = (point: LatLon | null | undefined): point is LatLon =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lon);

type Box = { minLat: number; maxLat: number; minLon: number; maxLon: number };

const boxOf = (points: LatLon[]): Box | null => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const point of points) {
    if (!isFinitePoint(point)) continue;
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLon = Math.min(minLon, point.lon);
    maxLon = Math.max(maxLon, point.lon);
  }
  return minLat === Infinity ? null : { minLat, maxLat, minLon, maxLon };
};

const overlaps = (a: Box, b: Box): boolean =>
  a.maxLat >= b.minLat && a.minLat <= b.maxLat && a.maxLon >= b.minLon && a.minLon <= b.maxLon;

/**
 * How far apart two boundaries are, edge to edge, in metres.
 *
 * The nearest of one ring's vertices to the other ring's edges. Not a true polygon distance —
 * two rings can approach each other mid-edge on both sides, which this reads as slightly
 * further apart than they are — but the shapes here are bunkers and greens measured against a
 * fairway tens of metres away, where the error is under a metre and the threshold is thirty.
 *
 * Zero is never returned for overlapping rings either; a bunker cut into a fairway reads as its
 * distance to the fairway's edge, which is well inside every margin that uses this.
 */
export const distanceBetweenRingsMeters = (a: LatLon[], b: LatLon[]): number => {
  if (a.length === 0 || b.length < 2) return Infinity;

  let nearest = Infinity;
  for (const vertex of a) {
    if (!isFinitePoint(vertex)) continue;
    nearest = Math.min(nearest, distanceToRingMeters(vertex, b));
  }
  return nearest;
};

/**
 * The ground a ring encloses, in square metres.
 *
 * The shoelace formula on a local equirectangular projection: over a bunker or a green the
 * curvature of the earth is far below the precision of the boundary itself, and the numbers
 * this produces are read as "is that plausible for a bunker", not surveyed.
 *
 * Zero for anything that is not a closed shape, so a stray two-point row reads as nothing
 * rather than as a sliver.
 */
export const ringAreaSquareMeters = (ring: LatLon[]): number => {
  const points = ring.filter(isFinitePoint);
  if (points.length < 3) return 0;

  const midLat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const lonScale = Math.cos((midLat * Math.PI) / 180) * METERS_PER_DEGREE_LAT;

  let twiceArea = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    // Wraps to the first point, which closes a ring that was not stored closed.
    const next = points[(index + 1) % points.length];
    const x1 = current.lon * lonScale;
    const y1 = current.lat * METERS_PER_DEGREE_LAT;
    const x2 = next.lon * lonScale;
    const y2 = next.lat * METERS_PER_DEGREE_LAT;
    twiceArea += x1 * y2 - x2 * y1;
  }

  // Absolute: a ring stored clockwise encloses the same ground as one stored the other way.
  return Math.abs(twiceArea) / 2;
};

/**
 * A representative point inside a ring, for anchoring a label — not a true polygon centroid.
 *
 * The plain average of the vertices. For the compact, roughly-convex shapes this draws (a
 * bunker, a pond) that lands inside the shape just as reliably as an area-weighted centroid
 * would, and a ring irregular enough that the two diverge is irregular enough that a label
 * position is the least of its problems.
 */
export const ringCentroid = (ring: LatLon[]): LatLon | null => {
  const points = ring.filter(isFinitePoint);
  if (points.length === 0) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lon: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
  };
};

/**
 * The boundaries that reach into one hole's frame, in draw order.
 *
 * **Cover** — fairway and trees — is taken from the rows filed under this hole, when there are
 * any. That is now a real answer rather than a guess: the ingest divides a strip of cover
 * spanning two holes along the line where one stops being the nearest and stores a row per
 * share, so every hole has its own and none carries a neighbour's (geodata-service's
 * `HoleCatchments` for OSM, `corridor.py` for the imagery analyser).
 *
 * Where a hole has no cover of its own the bounding-box test it always had applies instead: on
 * a course imported before the split, one 400 m ring is still the only fairway there is, and
 * drawing it on both holes beats leaving one of them bare. That fallback is per hole, so the
 * two kinds of data can sit side by side on one course and each hole gets the better answer
 * available to it — which is also why this needed no migration to ship.
 *
 * It is the fallback that draws the neighbour's grass, and the reason the cover half of this was
 * wrong for so long: a bounding box around a ring spanning two holes reaches both, so the box
 * test cannot tell "this hole's fairway" from "the next one's, seen edge-on".
 *
 * A hazard is a different question, asked in whichever of three ways the course is mapped
 * well enough to answer. Each is measured against Holms Golfklubb's 80 OSM bunkers, scored
 * against the hole each is filed under (drawn / on another hole / this hole's but missing):
 *
 * 1. **Near this hole's own fairway or green**, within {@link PLAY_AREA_MARGIN_METERS}.
 *    80 / 0 / 0. A hazard exists to guard mown ground, and the mown ground is already drawn
 *    with the hole's width and its dogleg in it — so nothing has to be assumed about either.
 * 2. **Nearer this hole's centreline than any other hole's**, within
 *    {@link CENTRELINE_MARGIN_METERS}. Also 80 / 0 / 0, for a hole whose fairway and green
 *    were never traced. The nearest-hole half is what carries it: the same 50 m without it
 *    draws three of the neighbour's bunkers.
 * 3. **Within `marginMeters` of the tee-to-green line**, for a hole that has neither.
 *    106 / 26 / 0 — this is what every hole used to get, and a quarter of what it draws
 *    belongs to somebody else. An 80 m half-width is simply wider than the gap between parallel holes,
 *    which no amount of tuning fixes: at 30 m it stops drawing foreign bunkers and starts
 *    losing twenty real ones instead.
 *
 * 1 and 2 are a union rather than a chain, so a hole with a green traced but no fairway keeps
 * its fairway bunkers through the centreline instead of losing them to a play area that only
 * covers the last hundred metres.
 *
 * A hole also has exactly one green. Where a neighbour's green boundary still lands inside
 * the margin — short holes running back to back — only the one closest to the green position
 * already resolved for this hole survives; the rest are dropped even though they passed the
 * distance test, the same way a second green *point* on this hole is never trusted over the
 * first (see `useHoleViewGeometry`).
 */
export const shapesNearHole = (
  shapes: HoleShape[],
  tees: LatLon[],
  green: LatLon | null,
  context: HoleSelectionContext = {}
): HoleShape[] => {
  const {
    coverAreas = [],
    pathAreas = [],
    playAreas = [],
    otherPlayAreas = [],
    centreline = null,
    otherCentrelines = [],
    marginMeters = HOLE_FRAME_MARGIN_METERS,
  } = context;
  const anchors = [...tees, ...(green ? [green] : [])].filter(isFinitePoint);
  const holeBox = boxOf(anchors);
  // With nothing to measure against there is no frame, and every polygon on the course
  // would qualify. Better to draw none than to paint the neighbouring holes over this one.
  if (!holeBox) return [];

  const latMargin = marginMeters / METERS_PER_DEGREE_LAT;
  const midLat = (holeBox.minLat + holeBox.maxLat) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180);
  // Near the poles the longitude margin would blow up; the hole itself is the sane fallback.
  const lonMargin = Math.abs(cosLat) > 1e-6 ? latMargin / Math.abs(cosLat) : latMargin;

  const frame: Box = {
    minLat: holeBox.minLat - latMargin,
    maxLat: holeBox.maxLat + latMargin,
    minLon: holeBox.minLon - lonMargin,
    maxLon: holeBox.maxLon + lonMargin,
  };

  // One segment per tee (there is usually just one), each running to the green — the
  // corridor a hazard is judged against. Without a green there is no corridor to measure
  // along, so the fallback below is distance to the tee points themselves.
  const corridors: Array<[LatLon, LatLon]> = green
    ? tees.filter(isFinitePoint).map((tee) => [tee, green])
    : [];

  const ownCoverIds = new Set(coverAreas.map((area) => area.id));
  const ownPathIds = new Set(pathAreas.map((area) => area.id));

  const ringsOf = (areas: HoleShape[]) =>
    areas.map((area) => area.ring.filter(isFinitePoint)).filter((ring) => ring.length >= 3);
  const playRings = ringsOf(playAreas);
  const otherPlayRings = ringsOf(otherPlayAreas);
  const ownLine = (centreline ?? []).filter(isFinitePoint);
  const otherLines = otherCentrelines
    .map((line) => line.filter(isFinitePoint))
    .filter((line) => line.length >= 2);

  const nearest = (distances: number[]) =>
    distances.length === 0 ? Infinity : Math.min(...distances);

  /**
   * Both tests below are "inside the margin, and nearer to mine than to anybody else's". The
   * margin decides whether a hazard belongs to any hole at all; the comparison decides which,
   * and it is the half that does the work — a bunker 2.8 m off the 7th fairway is also exactly
   * 30 m off the 6th, and only the comparison knows which of those two numbers means anything.
   *
   * Ties go to this hole. A hazard genuinely equidistant sits between the two, and drawing it
   * on both neighbours reads better than drawing it on neither.
   */
  const guardsThisHole = (ring: LatLon[]): boolean => {
    const own = nearest(playRings.map((play) => distanceBetweenRingsMeters(ring, play)));
    if (own > PLAY_AREA_MARGIN_METERS) return false;
    return own <= nearest(otherPlayRings.map((play) => distanceBetweenRingsMeters(ring, play)));
  };

  const onThisHolesLine = (ring: LatLon[]): boolean => {
    if (ownLine.length < 2) return false;
    const own = nearest(ring.map((vertex) => distanceToPolylineMeters(vertex, ownLine)));
    if (own > CENTRELINE_MARGIN_METERS) return false;
    return otherLines.every(
      (line) => own <= nearest(ring.map((vertex) => distanceToPolylineMeters(vertex, line)))
    );
  };

  const withinCorridor = (ring: LatLon[]): boolean => {
    if (corridors.length > 0) {
      return corridors.some(([from, to]) =>
        ring.some((vertex) => distanceToSegmentMeters(vertex, from, to) <= marginMeters)
      );
    }
    return anchors.some((anchor) => distanceToRingMeters(anchor, ring) <= marginMeters);
  };

  const withinFrame = shapes.filter((shape) => {
    if (!HOLE_VIEW_KINDS.includes(shape.kind)) return false;
    // Two vertices are a line, not a boundary; drawing one is a stray stroke on the map.
    if (shape.ring.length < 3) return false;
    if (HAZARD_KINDS.includes(shape.kind)) {
      const ring = shape.ring.filter(isFinitePoint);
      if (ring.length < 3) return false;
      if (playRings.length > 0 || ownLine.length >= 2) {
        return guardsThisHole(ring) || onThisHolesLine(ring);
      }
      return withinCorridor(ring);
    }
    // Cover (and paths) this hole owns outright, where the ingest divided it. Otherwise the box.
    const ownIds = PATH_KINDS.includes(shape.kind) ? ownPathIds : ownCoverIds;
    if (ownIds.size > 0) return ownIds.has(shape.id);
    const box = boxOf(shape.ring);
    return box !== null && overlaps(box, frame);
  });

  let closestGreenId: string | null = null;
  if (green) {
    let closestDistance = Infinity;
    for (const shape of withinFrame) {
      if (shape.kind !== 'green') continue;
      const distance = distanceToRingMeters(green, shape.ring);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestGreenId = shape.id;
      }
    }
  }

  return withinFrame
    .filter((shape) => shape.kind !== 'green' || shape.id === closestGreenId)
    .sort((a, b) => (SHAPE_DEPTH[a.kind] ?? 9) - (SHAPE_DEPTH[b.kind] ?? 9));
};

/**
 * Builds one hole's {@link HoleSelectionContext} out of a whole course's rows.
 *
 * Everything here is picked **by the hole number the row carries**, which is what the ingest
 * decided when it divided the course between the holes. Two jobs come out of that same set.
 *
 * As the cover this hole *draws*: one share per hole, so nothing is painted twice.
 *
 * As the mown ground a *hazard is measured against*: a bunker belongs to the hole whose fairway
 * or green it is nearest to, which only works if each hole's play area is its own. Before the
 * ingest divided cover these two jobs wanted opposite answers from the same rows — one fairway
 * ring had to be drawn on both holes it spanned, while as a measuring stick it had to belong to
 * exactly one — and only the second could have it. They now agree.
 */
export const holeSelectionContext = (
  shapes: Array<HoleShape & { holeNumber: number }>,
  centrelines: HoleCentreline[],
  holeNumber: number
): HoleSelectionContext => ({
  coverAreas: shapes.filter(
    (shape) => shape.holeNumber === holeNumber && COVER_KINDS.includes(shape.kind)
  ),
  pathAreas: shapes.filter(
    (shape) => shape.holeNumber === holeNumber && PATH_KINDS.includes(shape.kind)
  ),
  playAreas: shapes.filter(
    (shape) => shape.holeNumber === holeNumber && PLAY_AREA_KINDS.includes(shape.kind)
  ),
  otherPlayAreas: shapes.filter(
    (shape) => shape.holeNumber !== holeNumber && PLAY_AREA_KINDS.includes(shape.kind)
  ),
  centreline: centrelines.find((line) => line.holeNumber === holeNumber)?.path ?? null,
  otherCentrelines: centrelines
    .filter((line) => line.holeNumber !== holeNumber)
    .map((line) => line.path),
});
